import { useQuery } from "@tanstack/react-query";
import { useOspApiClientProvider } from "./useOspApiClientProvider";
import {
  OpenDataTypeEnum,
  type SearchOpenData,
  type SearchOpenDataPaginationResponse,
} from "@/src/features/api/openData";
import { I18nUtil } from "@/src/utils";
import { QUERY_KEYS } from "@config/constant";

export interface UseSearchOptions {
  /** 搜索关键词 */
  keyword: string;
  /** 搜索类型（THEME / TOKEN / STOCK 等） */
  type: OpenDataTypeEnum;
  /** 搜索子类型 */
  subType?: OpenDataTypeEnum;
  /** 市场（US/HK），仅 type | subType 为 STOCK 时有效 */
  market?: string;
  /** 每页数量，默认 20 */
  limit?: number;
  /** 是否启用请求 */
  enabled?: boolean;
}

/**
 * 调用 OpenData /search 接口的搜索 Hook
 * - 单类型搜索，返回扁平列表（非分组）
 */
export function useSearch({
  keyword,
  type,
  subType,
  market,
  limit = 20,
  enabled = true,
}: UseSearchOptions) {
  const { openDataApi } = useOspApiClientProvider();
  const locale = I18nUtil.getCurrentLocale();
  const language = I18nUtil.getBackgroundLanguageKey(locale);

  const { data, isFetching, isPending, error } = useQuery({
    queryKey: [
      QUERY_KEYS.OPEN_DATA_SEARCH,
      keyword,
      type,
      subType,
      market,
      language,
    ],
    enabled:
      !!openDataApi && !!keyword && keyword.trim() !== "" && (enabled ?? true),
    queryFn: async (): Promise<SearchOpenData[]> => {
      const res = await openDataApi!.search.search(
        {
          type,
          sub_type: subType,
          keyword: keyword.trim(),
          operator: "MATCH",
          limit,
          language,
          market,
        },
        {},
      );
      const pagination = (res.data as SearchOpenDataPaginationResponse)?.data;
      return pagination?.rows ?? [];
    },
  });

  return {
    data: data ?? [],
    isLoading: isPending || isFetching,
    error,
  };
}
